<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { Copy, FileText, Folder, ChevronDown, Type } from 'lucide-vue-next';
import demoShareImgDefault from '@/assets/images/demo-share.png';

const props = defineProps<{
  thumbImage?: string;
  autoScroll?: boolean;
}>();

const emit = defineEmits(['close']);
const scrollerRef = ref<HTMLElement | null>(null);

const slowScrollTo = (element: HTMLElement, target: number, duration: number) => {
  const start = element.scrollLeft;
  const change = target - start;
  let currentTime = 0;
  const increment = 20;

  const animateScroll = () => {
    currentTime += increment;
    const val = easeInOutQuad(currentTime, start, change, duration);
    element.scrollLeft = val;
    if (currentTime < duration) {
      setTimeout(animateScroll, increment);
    }
  };
  animateScroll();
};

const easeInOutQuad = (t: number, b: number, c: number, d: number): number => {
  t /= d/2;
  if (t < 1) return c/2*t*t + b;
  t--;
  return -c/2 * (t*(t-2) - 1) + b;
};

onMounted(() => {
  if (props.autoScroll && scrollerRef.value) {
    setTimeout(() => {
      if (scrollerRef.value) {
        const maxScroll = scrollerRef.value.scrollWidth - scrollerRef.value.clientWidth;
        slowScrollTo(scrollerRef.value, maxScroll, 2000); // 2 seconds for a slow sweep
      }
    }, 800);
  }
});
</script>

<template>
  <div class="ios-native-overlay">
    <div class="ios-backdrop-blur" @click="emit('close')"></div>
    
    <div class="ios-sheet-card">
      <div class="ios-pull-bar"></div>
      
      <!-- Top Preview Section -->
      <div class="ios-preview-header">
        <div class="ios-preview-thumb" :style="{ backgroundImage: `url(${thumbImage || demoShareImgDefault})` }"></div>
        <div class="ios-preview-text">
          <span class="ios-preview-title">bet-pic</span>
          <span class="ios-preview-meta"><span class="text-green-500">PNG Image</span> • 435 KB</span>
        </div>
      </div>

      <!-- App Row -->
      <div class="ios-section-divider"></div>
      <div class="ios-apps-row-scroller" ref="scrollerRef">
        <div class="ios-app-unit"><div class="a-icon airdrop"><i class="pi pi-bullseye"></i></div><span>AirDrop</span></div>
        <div class="ios-app-unit"><div class="a-icon messages"><i class="pi pi-comment"></i></div><span>Messages</span></div>
        <div class="ios-app-unit"><div class="a-icon mail"><i class="pi pi-envelope"></i></div><span>Mail</span></div>
        <div class="ios-app-unit"><div class="a-icon whatsapp"><i class="pi pi-whatsapp"></i></div><span>WhatsApp</span></div>
        <div class="ios-app-unit"><div class="a-icon telegram"><i class="pi pi-send"></i></div><span>Telegram</span></div>
        <div class="ios-app-unit"><div class="a-icon facebook"><i class="pi pi-facebook"></i></div><span>Facebook</span></div>
        <div class="ios-app-unit"><div class="a-icon x-social"><i class="pi pi-twitter"></i></div><span>X</span></div>
        <div class="ios-app-unit"><div class="a-icon more"><i class="pi pi-ellipsis-h"></i></div><span>More</span></div>
      </div>

      <!-- Actions Row -->
      <div class="ios-section-divider"></div>
      <div class="ios-actions-row">
        <div class="ios-action-unit"><div class="act-icon-sq"><Copy :size="22" /></div><span>Copy</span></div>
        <div class="ios-action-unit"><div class="act-icon-sq"><FileText :size="22" /></div><span>New Quick Note</span></div>
        <div class="ios-action-unit"><div class="act-icon-sq"><Folder :size="22" /></div><span>Save to Files</span></div>
        <div class="ios-action-unit"><div class="act-icon-sq"><ChevronDown :size="22" /></div><span>View More</span></div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ios-native-overlay { position: absolute; inset: 0; z-index: 300; display: flex; flex-direction: column; justify-content: flex-end; }
.ios-backdrop-blur { position: absolute; inset: 0; background: rgba(0,0,0,0.3); }

.ios-sheet-card { 
  background: rgba(245, 245, 247, 0.85); 
  backdrop-filter: blur(30px) saturate(180%); 
  border-radius: 24px 24px 0 0; 
  padding: 8px 16px 40px; 
  color: #000; 
  position: relative; 
  z-index: 301; 
  box-shadow: 0 -10px 40px rgba(0,0,0,0.2); 
  border: 1px solid rgba(255,255,255,0.3);
}

.ios-pull-bar { width: 36px; height: 5px; background: rgba(0,0,0,0.1); border-radius: 10px; margin: 8px auto 20px; }

.ios-preview-header { display: flex; align-items: center; gap: 12px; padding: 0 4px 16px; }
.ios-preview-thumb { width: 56px; height: 56px; background-size: cover; background-position: center; border-radius: 12px; border: 1px solid rgba(0,0,0,0.05); background-color: #fff; }
.ios-preview-text { display: flex; flex-direction: column; gap: 1px; }
.ios-preview-title { font-size: 14px; color: #000; font-weight: 600; }
.ios-preview-meta { font-size: 11px; color: #888; font-weight: 400; }

.ios-section-divider { height: 1px; background: rgba(0,0,0,0.05); margin: 0 -16px; }

.ios-apps-row-scroller { display: flex; gap: 20px; padding: 20px 0; overflow-x: auto; scrollbar-width: none; }
.ios-apps-row-scroller::-webkit-scrollbar { display: none; }

.ios-app-unit { display: flex; flex-direction: column; align-items: center; gap: 6px; min-width: 64px; }
.ios-app-unit span { font-size: 11px; color: #333; font-weight: 400; }

.a-icon { width: 58px; height: 58px; border-radius: 13px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(0,0,0,0.08); position: relative; }
.a-icon i { font-size: 32px !important; }

/* Icon Brand Colors & Gradients */
.airdrop { background: linear-gradient(180deg, #5fc9f8 0%, #007aff 100%); color: #fff; }
.messages { background: linear-gradient(180deg, #62ef7d 0%, #2bd34d 100%); color: #fff; }
.mail { background: linear-gradient(180deg, #55a8fb 0%, #1d62f0 100%); color: #fff; }
.whatsapp { background: #25D366; color: #fff; }
.telegram { background: #0088cc; color: #fff; }
.facebook { background: #1877F2; color: #fff; }
.x-social { background: #000; color: #fff; }
.more { background: #E5E5EA; color: #8E8E93; }

.ios-actions-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; padding-top: 20px; }
.ios-action-unit { display: flex; flex-direction: column; align-items: center; gap: 6px; text-align: center; }
.ios-action-unit span { font-size: 10px; color: #333; font-weight: 500; line-height: 1.1; }

.act-icon-sq { 
  width: 58px; height: 58px; 
  background: rgba(0,0,0,0.05); 
  border-radius: 50%; 
  display: flex; align-items: center; justify-content: center; 
  color: #000; 
  margin-bottom: 2px;
}
</style>
